import React from 'react';

import { useStateWithStorage } from '../utils/storage';

interface IThemeContext {
	theme: string;
	preference: string;
	setPreference: (preference: string) => void;
}

const defaultTheme: IThemeContext = {
	theme: '',
	preference: '',
	setPreference: () => {},
};

export const ThemeContext = React.createContext<IThemeContext>(defaultTheme);

// Getting dark mode information from OS!
//	You need macOS Mojave + Safari Technology Preview Release 68 to test this currently.
const supportsDarkMode = () => {
	return window.matchMedia('(prefers-color-scheme: dark)').matches === true;
};

type ThemeProviderProps = {
	children: React.ReactNode;
}

export const ThemeProvider = (props: ThemeProviderProps) => {
	const { children } = props;

	const [initialized, setInitialized] = React.useState<boolean>(false);
	const [preference, setPreference] = useStateWithStorage<string>(
		'themePreference',
		'',
		{
			rememberForever: true,
			onInitialize: () => setInitialized(true)
		}
	);
	const [theme, setTheme] = useStateWithStorage<string>('theme', '');

	React.useEffect(() => {
		// Listen for theme changes from other tabs
		window.addEventListener('storage', handleStorageEvent);
		// Cleanup
		return () => {
			window.removeEventListener('storage', handleStorageEvent);
		};

		function handleStorageEvent(event: StorageEvent): void {
			if (event.key === 'themePreference') {
				const newTheme: string = event.newValue ? JSON.parse(event.newValue) : '';
				if (newTheme === 'auto')
					setPreference('auto');
				else if (newTheme === 'dark')
					setPreference('dark');
				else
					setPreference('light');
			};
		}
	}, [setPreference]);

	React.useEffect(() => {
		if (!initialized) return;
		if (preference === 'auto')
			setTheme(supportsDarkMode() ? 'dark' : 'light');
		else
			setTheme(preference === 'dark' ? 'dark' : 'light');
	}, [initialized, preference]);

	const providerValue: IThemeContext = { theme, preference, setPreference };

	return (
		<ThemeContext.Provider value={providerValue}>
			{children}
		</ThemeContext.Provider>
	);
};
